import React, { useState, useEffect, useRef } from "react";
import { formatError } from "../utils/api";
import {
  FileText, Plus, Edit, Trash2, X, Upload, Sparkles, Check,
  Download, ChevronDown, RefreshCw, Loader, Info, Settings,
  Table, Eye, Copy
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("manna_access_token")}` } });

const REPORT_TYPES = [
  { value: "billing",       label: "Billing / Invoicing" },
  { value: "school_indent", label: "School Indent" },
  { value: "attendance",    label: "Attendance" },
  { value: "custom",        label: "Custom" },
];

const TEMPLATE_METHODS = [
  { value: "excel_upload", label: "Upload Excel Template (AI analyses it)", icon: Upload },
  { value: "manual",       label: "Build manually (column-by-column)",       icon: Table },
];

// ─── SEED FORMATS ─────────────────────────────────────────────────────────────
const SEED_FORMATS = [
  {
    name: "DEO Billing Format",
    report_type: "billing",
    description: "Day-wise voucher for District Education Officer (Primary 1-5, Upper 6-8, Upper 9-10 — separate sheets)",
    template_method: "builtin",
    builtin_key: "deo_voucher",
    is_active: true,
  },
  {
    name: "DEO School-wise Billing",
    report_type: "billing",
    description: "School-wise detailed billing with caste breakdown (General/SC/ST per band)",
    template_method: "builtin",
    builtin_key: "deo_schoolwise",
    is_active: true,
  },
];

export default function ReportFormats() {
  const [formats, setFormats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);   // null | "create" | "edit" | "view"
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", report_type: "billing", description: "", template_method: "excel_upload", config: {}, is_active: true });
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [availableFields, setAvailableFields] = useState({});
  const fileRef = useRef();

  useEffect(() => {
    fetchFormats();
    axios.get(`${API}/api/report-formats/available-fields`, auth())
      .then(r => setAvailableFields(r.data || {})).catch(() => {});
  }, []);

  const fetchFormats = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/report-formats`, auth());
      setFormats(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const handleSave = async () => {
    setError("");
    if (!form.name) { setError("Name is required"); return; }
    try {
      const payload = { ...form, config: aiResult?.config || form.config || {} };
      if (selected) {
        await axios.put(`${API}/api/report-formats/${selected.id}`, payload, auth());
      } else {
        await axios.post(`${API}/api/report-formats`, payload, auth());
      }
      setMsg("Saved!"); setModal(null); fetchFormats();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report format?")) return;
    await axios.delete(`${API}/api/report-formats/${id}`, auth());
    fetchFormats();
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;
    setAnalyzing(true); setAiResult(null); setError("");
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const r = await axios.post(`${API}/api/report-formats/analyze-excel`, {
          file_base64: e.target.result,
          filename: file.name,
          report_type: form.report_type,
          name_hint: form.name || file.name.replace(/\.(xlsx?)/i, ""),
        }, auth());
        setAiResult(r.data);
        setForm(f => ({
          ...f,
          name: f.name || r.data.suggested_name,
          description: f.description || r.data.description,
          config: r.data.config || {},
        }));
        setMsg("AI analyzed your Excel template successfully!");
      } catch (e2) { setError(formatError(e2)); }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  const handleTestGenerate = async (fmt) => {
    const today = new Date();
    const { url, ...rest } = { url: null };
    try {
      const response = await fetch(
        `${API}/api/report-formats/${fmt.id}/generate`,
        {
          method: "POST",
          headers: { ...auth().headers, "Content-Type": "application/json" },
          body: JSON.stringify({ month: today.getMonth() + 1, year: today.getFullYear() }),
        }
      );
      if (!response.ok) { setError("Generation failed — check format config"); return; }
      const blob = await response.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `test_${fmt.name.replace(/\s/g,"_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(formatError(e)); }
  };

  const openCreate = () => {
    setSelected(null);
    setForm({ name: "", report_type: "billing", description: "", template_method: "excel_upload", config: {}, is_active: true });
    setAiResult(null); setError("");
    setModal("create");
  };

  const openEdit = (fmt) => {
    setSelected(fmt);
    setForm({ name: fmt.name, report_type: fmt.report_type, description: fmt.description || "", template_method: fmt.template_method || "manual", config: fmt.config || {}, is_active: fmt.is_active !== false });
    setAiResult(null); setError("");
    setModal("edit");
  };

  const fields = availableFields[form.report_type] || {};

  return (
    <div className="space-y-5 max-w-5xl" data-testid="report-formats">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
            <span className="w-9 h-9 bg-[#E8956D]/15 rounded-xl flex items-center justify-center">
              <FileText size={18} className="text-[#E8956D]" />
            </span>
            Report Formats
          </h2>
          <p className="text-sm text-[#8A7E73] mt-0.5 ml-12">
            Create custom report templates. Each format defines how data is structured and exported.
            Download any report in any saved format.
          </p>
        </div>
        <button onClick={openCreate} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2">
          <Plus size={15} /> New Format
        </button>
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm flex items-center gap-2"><Check size={13}/>{msg}</div>}

      {/* How it works */}
      <div className="bg-gradient-to-r from-[#5C5147]/5 to-[#E8956D]/5 border border-[#E8956D]/20 rounded-xl p-4">
        <p className="font-bold text-[#3A322C] text-sm mb-2">How Report Formats work</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-[#5C5147]">
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-[#E8956D] text-white rounded-full flex items-center justify-center font-bold shrink-0 mt-0.5">1</span>
            <span><strong>Create a format</strong> — upload your existing Excel template or build column-by-column. AI reads the Excel and maps the columns automatically.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-[#E8956D] text-white rounded-full flex items-center justify-center font-bold shrink-0 mt-0.5">2</span>
            <span><strong>Save it</strong> — name it (e.g. "DEO Format", "Head Office Format", "Secretariat Format"). Multiple formats can exist for the same report type.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-[#E8956D] text-white rounded-full flex items-center justify-center font-bold shrink-0 mt-0.5">3</span>
            <span><strong>Use it anywhere</strong> — when downloading any report (billing, indent, etc.) a popup lets you choose which format to use.</span>
          </div>
        </div>
      </div>

      {/* Formats list */}
      {loading ? (
        <div className="text-center py-12"><Loader size={24} className="animate-spin text-[#E8956D] mx-auto" /></div>
      ) : formats.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E5E1DB] rounded-2xl">
          <FileText size={40} className="mx-auto mb-3 text-[#C4BAAF]" />
          <p className="font-bold text-[#3A322C]">No formats yet</p>
          <p className="text-sm text-[#8A7E73] mt-1">Create your first report format by clicking "New Format"</p>
        </div>
      ) : (
        <div className="space-y-3">
          {formats.map(fmt => (
            <div key={fmt.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4" data-testid={`format-${fmt.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-bold text-[#3A322C]">{fmt.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      fmt.report_type === "billing" ? "bg-[#E8956D]/10 text-[#E8956D]" :
                      fmt.report_type === "school_indent" ? "bg-green-100 text-green-700" :
                      "bg-[#F4F3F0] text-[#8A7E73]"
                    }`}>
                      {REPORT_TYPES.find(t => t.value === fmt.report_type)?.label || fmt.report_type}
                    </span>
                    {fmt.template_method === "excel_upload" && <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">AI-analyzed</span>}
                    {fmt.template_method === "builtin" && <span className="text-[10px] bg-[#EDF2EE] text-[#637C64] px-2 py-0.5 rounded-full">Built-in</span>}
                    {fmt.usage_count > 0 && <span className="text-[10px] text-[#8A7E73]">Used {fmt.usage_count}×</span>}
                  </div>
                  {fmt.description && <p className="text-sm text-[#8A7E73]">{fmt.description}</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handleTestGenerate(fmt)}
                    className="flex items-center gap-1 text-xs border border-[#637C64] text-[#637C64] rounded-lg px-3 py-1.5 font-bold hover:bg-[#EDF2EE]">
                    <Download size={11} /> Test
                  </button>
                  <button onClick={() => openEdit(fmt)} className="text-[#8A7E73] hover:text-[#E8956D] p-1.5 rounded hover:bg-[#F4F3F0]">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDelete(fmt.id)} className="text-[#8A7E73] hover:text-[#C2624E] p-1.5 rounded hover:bg-[#F4F3F0]">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {(modal === "create" || modal === "edit") && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white z-10">
              <h3 className="font-bold text-lg text-[#3A322C]">{modal === "edit" ? "Edit" : "New"} Report Format</h3>
              <button onClick={() => setModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-5">

              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Format Name *</label>
                  <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    placeholder="e.g. DEO Billing Format, Head Office Format"
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Report Type *</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.report_type} onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))}>
                    {REPORT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Template Method</label>
                  <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]"
                    value={form.template_method} onChange={e => setForm(f => ({ ...f, template_method: e.target.value }))}>
                    {TEMPLATE_METHODS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Description</label>
                  <textarea rows={2} className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none"
                    placeholder="When to use this format, which office it's for..."
                    value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>

              {/* Excel Upload + AI Analysis */}
              {form.template_method === "excel_upload" && (
                <div className="border-2 border-dashed border-[#E8956D]/40 rounded-xl p-5 bg-[#E8956D]/3 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles size={16} className="text-[#E8956D]" />
                    <span className="font-bold text-[#3A322C] text-sm">Upload Excel Template → AI Analysis</span>
                  </div>
                  <p className="text-xs text-[#8A7E73]">
                    Upload your existing Excel report (any format). Claude AI will analyze the column structure, formulas, headers and generate a reusable config automatically.
                  </p>
                  <button onClick={() => fileRef.current?.click()} disabled={analyzing}
                    className="flex items-center gap-2 bg-[#5C5147] text-white rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-60">
                    {analyzing ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
                    {analyzing ? "AI is analyzing..." : "Choose Excel File"}
                  </button>
                  <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden"
                    onChange={e => { if (e.target.files[0]) handleExcelUpload(e.target.files[0]); e.target.value = ""; }} />

                  {aiResult && (
                    <div className="bg-[#EDF2EE] rounded-lg p-3 text-xs text-[#637C64]">
                      <p className="font-bold mb-1 flex items-center gap-1"><Check size={12} /> AI Analysis Complete</p>
                      <p>Detected {aiResult.config?.sheets?.length || 0} sheet(s). Config saved — you can save the format now.</p>
                      {aiResult.config?.sheets?.map((s, i) => (
                        <p key={`idx-${i}`} className="mt-0.5">• Sheet: "{s.sheet_name}" — {s.columns?.length || 0} columns, group by: {s.group_by || "none"}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Available fields reference */}
              {Object.keys(fields).length > 0 && (
                <details className="border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <summary className="px-4 py-3 text-sm font-bold text-[#3A322C] cursor-pointer flex items-center gap-2 bg-[#F4F3F0]">
                    <Info size={13} className="text-[#8A7E73]" />
                    Available data fields for "{REPORT_TYPES.find(t=>t.value===form.report_type)?.label}"
                  </summary>
                  <div className="p-4 grid grid-cols-2 gap-1.5">
                    {Object.entries(fields).map(([key, desc]) => (
                      <div key={key} className="flex items-start gap-1.5">
                        <code className="text-[10px] bg-[#F4F3F0] text-[#E8956D] px-1.5 py-0.5 rounded font-mono shrink-0">{key}</code>
                        <span className="text-[10px] text-[#8A7E73]">{desc}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-lg px-3 py-2 text-sm">{error}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6 sticky bottom-0 bg-white pt-3 border-t border-[#E5E1DB]">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] text-[#3A322C] rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2">
                <Check size={15} /> Save Format
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
